import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ChatProvider } from './contexts/ChatContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import ChatButton from './components/chat/ChatButton';
import ChatNotificationContainer from './components/chat/ChatNotificationContainer';

// Import des composants
import Home from './components/Home';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import Register from './components/Register';
import { AdminLayout, AdminHome, AdminUsers, AccountVerification, AdminMoverRegistrationDetails, AdminUserDetails, AdminMoves, AdminMoveDetails, AdminPayments, AdminPaymentDetails, AdminLogs, AdminSettings, AdminMessages, AdminTickets, AdminAnalytics, AdminReports, AdminNotifications, AdminFeatures, AdminActivityLogs, AdminFrauds, AdminModeration, AdminSubscriptions, AdminSystemConfig, AdminRolesPermissions, AdminEmailTemplates, AdminSystemMonitoring, AdminNegotiations, AdminReferrals, AdminReviews, AdminMarketing, AdminCategories } from './components/admin';
import ClientDashboard from './components/ClientDashboard';
import DemenageurLayout from './components/demenageur/DemenageurLayout';
import PublicRoute from './components/PublicRoute';
import MesDevis from './components/MesDevis';
import { DemHome, DemMissions, DemDisponibilites, DemMessages, DemProfil, DemParametres } from './components/demenageur';
// Import des composants de contrat
import ContractRoutes from './routes/contractRoutes';
import TransactionDetails from './pages/demenageur/TransactionDetails';
import MissionDetails from './components/demenageur/MissionDetails';
import { ClientHome, ClientMoves, ClientQuotes, ClientProfile, ClientSettings, ClientSearch, ClientQuoteDetails, ClientTickets, ClientTicketDetails, Inventory, ClientDevis } from './components/client';
import NotificationsPage from './pages/NotificationsPage';
import ClientMoveDetails from './components/client/MoveDetails';
import SearchQuote from './components/client/SearchQuote';
import AdminQuickLoginModal from './components/AdminQuickLoginModal';
import AdminSecretTapZone from './components/AdminSecretTapZone';
import { ADMIN_QUICK_LOGIN_EVENT, isMobileWebShortcutEnabled } from './utils/adminSecretAccess';
import AuthCallback from './components/AuthCallback';
import TestNotificationsPage from './pages/TestNotificationsPage';
import ChatPage from './pages/ChatPage';
import PaiementPage from './components/demenageur/Paiements';
import DemenageurChat from './components/demenageur/DemenageurChat';
import TestChatNotification from './components/chat/TestChatNotification';
import { setNavigator } from './services/navigator';
import VerifyEmail from './components/VerifyEmail';
import { SystemSettingsProvider, useSystemSettings } from './contexts/SystemSettingsContext';
import MaintenanceBanner from './components/MaintenanceBanner';
import SpaPathRecovery from './components/SpaPathRecovery';

// Composant pour protéger les routes
const ProtectedRoute = ({ children, requiredRole }) => {
  const token = localStorage.getItem('token');
  const userRole =
    localStorage.getItem('userType') ||
    localStorage.getItem('userRole');
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  if (requiredRole && userRole !== requiredRole) {
    // Rediriger vers le tableau de bord correspondant au rôle de l'utilisateur
    const dashboardPath = userRole === 'admin' ? '/admin' : `/${userRole || 'client'}`;
    return <Navigate to={dashboardPath} replace />;
  }
  
  return children;
};

function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings, loading: settingsLoading } = useSystemSettings();
  const [adminOpen, setAdminOpen] = React.useState(false);
  const [mobileAdminShortcut, setMobileAdminShortcut] = React.useState(false);

  React.useEffect(() => {
    setNavigator(navigate);
    const onKeyDown = (e) => {
      const isA = e.code === 'KeyA' || e.key?.toLowerCase() === 'a';
      const isShift = e.shiftKey === true;
      const isCtrlOrMeta = e.ctrlKey === true || e.metaKey === true; // Ctrl (Win/Linux) ou Cmd (macOS)
      if (isA && isShift && isCtrlOrMeta) {
        e.preventDefault();
        setAdminOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate]);

  React.useEffect(() => {
    const syncMobileShortcut = () => setMobileAdminShortcut(isMobileWebShortcutEnabled());
    syncMobileShortcut();
    const mqNarrow = window.matchMedia('(max-width: 768px)');
    const mqCoarse = window.matchMedia('(pointer: coarse)');
    mqNarrow.addEventListener('change', syncMobileShortcut);
    mqCoarse.addEventListener('change', syncMobileShortcut);
    return () => {
      mqNarrow.removeEventListener('change', syncMobileShortcut);
      mqCoarse.removeEventListener('change', syncMobileShortcut);
    };
  }, []);

  React.useEffect(() => {
    const onMobileShortcut = () => setAdminOpen(true);
    window.addEventListener(ADMIN_QUICK_LOGIN_EVENT, onMobileShortcut);
    return () => window.removeEventListener(ADMIN_QUICK_LOGIN_EVENT, onMobileShortcut);
  }, []);

  const handleAdminSuccess = () => {
    setAdminOpen(false);
    navigate('/admin');
  };

  const userRole =
    typeof window !== 'undefined'
      ? localStorage.getItem('userType') || localStorage.getItem('userRole')
      : null;
  const showMaintenanceOffset =
    !settingsLoading &&
    settings?.maintenance &&
    userRole !== 'admin' &&
    !location.pathname.startsWith('/admin');

  return (
    <>
      <MaintenanceBanner />
      <div className={`App${showMaintenanceOffset ? ' app-with-maintenance-banner' : ''}`}>
        <SpaPathRecovery />
        <Routes>
          {/* Routes publiques */}
          <Route path="/" element={
            <PublicRoute>
              <Home />
            </PublicRoute>
          } />
          <Route path="/contact" element={<Navigate to="/#contact" replace />} />
          <Route path="/services" element={<Navigate to="/" replace />} />
          <Route path="/devis" element={<Navigate to="/" replace />} />
          <Route path="/mes-devis" element={<MesDevis />} />
          
          {/* Routes accessibles uniquement aux utilisateurs non connectés */}
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />
          
          <Route path="/forgot-password" element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          } />
          
          {/* Route de callback pour l'authentification sociale */}
          <Route path="/auth/callback" element={
            <PublicRoute>
              <AuthCallback />
            </PublicRoute>
          } />
          
          <Route path="/register" element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          } />
          
          {/* Route de vérification d'email */}
          <Route path="/verify-email" element={
            <PublicRoute>
              <VerifyEmail />
            </PublicRoute>
          } />
          
          {/* Routes protégées - Admin */}
          <Route path="/admin/*" element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route index element={<AdminHome />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="account-verification" element={<AccountVerification />} />
            <Route path="mover-registration/:id" element={<AdminMoverRegistrationDetails />} />
            <Route path="users/:id" element={<AdminUserDetails />} />
            <Route path="moves" element={<AdminMoves />} />
            <Route path="moves/:id" element={<AdminMoveDetails />} />
            <Route path="negotiations" element={<AdminNegotiations />} />
            <Route path="referrals" element={<AdminReferrals />} />
            <Route path="promotions" element={<Navigate to="/admin/referrals" replace />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="marketing" element={<AdminMarketing />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="payments/:id" element={<AdminPaymentDetails />} />
            <Route path="logs" element={<AdminLogs />} />
            <Route path="tickets" element={<AdminTickets />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="features" element={<AdminFeatures />} />
            <Route path="activity-logs" element={<AdminActivityLogs />} />
            <Route path="frauds" element={<AdminFrauds />} />
            <Route path="moderation" element={<AdminModeration />} />
            <Route path="subscriptions" element={<AdminSubscriptions />} />
            <Route path="system-config" element={<AdminSystemConfig />} />
            <Route path="system-monitoring" element={<AdminSystemMonitoring />} />
            <Route path="roles-permissions" element={<AdminRolesPermissions />} />
            <Route path="email-templates" element={<AdminEmailTemplates />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
          
          {/* Routes protégées - Client */}
          <Route path="/client/*" element={
            <ProtectedRoute requiredRole="client">
              <ClientDashboard />
            </ProtectedRoute>
          }>
            <Route index element={<ClientHome />} />
            <Route path="moves" element={<ClientMoves />} />
            <Route path="moves/:id" element={<ClientMoveDetails />} />
            <Route path="quotes" element={<ClientQuotes />} />
            <Route path="quotes/:id" element={<ClientQuoteDetails />} />
            <Route path="profile" element={<ClientProfile />} />
            <Route path="settings" element={<ClientSettings />} />
            <Route path="search" element={<ClientSearch />} />
            <Route path="search/quote" element={<SearchQuote />} />
            <Route path="devis" element={<ClientDevis />} />
            <Route path="tickets" element={<ClientTickets />} />
            <Route path="tickets/:id" element={<ClientTicketDetails />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="test-notifications" element={<TestNotificationsPage />} />
            <Route path="test-chat-notifications" element={<TestChatNotification />} />
            <Route path="messages" element={
              <ProtectedRoute>
                <ChatPage />
              </ProtectedRoute>
            } />
            <Route path="messages/quote/:quoteId" element={
              <ProtectedRoute>
                <ChatPage />
              </ProtectedRoute>
            } />
          </Route> {/* Close client routes */}
          
          {/* Chat routes outside client area */}
          <Route path="/messages" element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          } />
          <Route path="/messages/quote/:quoteId" element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          } />
          
          {/* Routes protégées - Déménageur */}
          <Route element={
            <ProtectedRoute requiredRole="demenageur">
              <DemenageurLayout />
            </ProtectedRoute>
          }>
            <Route path="/demenageur" element={<DemHome />} />
            <Route path="/demenageur/missions" element={<DemMissions />} />
            <Route path="/demenageur/missions/:id" element={<MissionDetails />} />
            <Route path="/demenageur/disponibilites" element={<DemDisponibilites />} />
            <Route path="/demenageur/contrat/*" element={<ContractRoutes />} />
            <Route path="/demenageur/messages" element={<DemMessages />} />
            <Route path="/demenageur/profil" element={<DemProfil />} />
            <Route path="/demenageur/parametres" element={<DemParametres />} />
            <Route path="/demenageur/chat" element={<DemenageurChat />} />
            <Route path="/demenageur/transactions/:id" element={<TransactionDetails />} />
            <Route path="/demenageur/zone-intervention" element={<div>Zone d'intervention</div>} />
            <Route path="/demenageur/badges" element={<div>Badges et récompenses</div>} />
            <Route path="/demenageur/paiements" element={<PaiementPage/>} />
          </Route>
          
          {/* Route par défaut */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <AdminQuickLoginModal open={adminOpen} onClose={() => setAdminOpen(false)} onSuccess={handleAdminSuccess} />
        {mobileAdminShortcut ? (
          <AdminSecretTapZone onTrigger={() => setAdminOpen(true)} />
        ) : null}
      </div>
    </>
  );
}

// Composant principal du contenu de l'application
const AppContent = () => {
  const { language } = useLanguage();
  
  // Mettre à jour la langue du document
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);
  
  return (
    <SystemSettingsProvider>
    <NotificationProvider>
      <ChatProvider>
        <AppShell />
        <ChatButton />
        <ChatNotificationContainer />
        <ToastContainer
          position="bottom-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={language === 'ar'}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </ChatProvider>
    </NotificationProvider>
    </SystemSettingsProvider>
  );
};

// Composant pour envelopper l'application avec les fournisseurs de contexte
const AppWithProviders = () => {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
};

function App() {
  return <AppWithProviders />;
}

export default App;
